import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { RoleContext } from '@/app/api/common';
import { store, useAppSelector } from '@/app/store';
import { checkNoError } from '@/features/channels/channelsSlice';
import {
  buildGroupRecord,
  copyGroupSettings,
  diffGroupMembers,
  getEmptyGroupSettings,
  getMoveProjectBody,
  getProjectGroupById,
  getRemoveGroupRecord,
  groupHasSettings,
  groupRemoveMode,
  groupSettingDefs,
  PROJECT_GROUP_NAME_MAX,
  ProjectGroupSettings,
  ProjectGroupWithStats,
  validateGroupName,
} from '@/features/projectGroups/projectGroupsSlice';
import { getProjectById } from '@/features/projects/projectsSlice';

import Button from '@/components/Common/Button';
import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input';
import { Modal } from '@/components/Common/Modals';

import { CatHeader } from './Project';

export interface ProjectGroupEditorProps {
  // null = a new group
  group:				ProjectGroupWithStats | null;
  onClose:				() => any;
}

// Task-80 §4.2 — create / rename a group, pick its projects, edit what its
// members inherit, and delete or disband it. Membership stays a project
// attribute (§2.1): the picker saves each project that joins or leaves with
// its own POST /hotels.json, after the group itself is saved.
const ProjectGroupEditor = (props: ProjectGroupEditorProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const role = React.useContext(RoleContext);

  const logins = useAppSelector((state) => state.logins);
  const groups = useAppSelector((state) => state.projectGroups.groups);
  const projects = useAppSelector((state) => state.projects.projects);

  const isNew = !props.group;
  const storedMembers = props.group ? props.group.projects : [];

  const [name, setName] = React.useState(props.group ? props.group.name : "");
  const [settings, setSettings] = React.useState(
    props.group ? copyGroupSettings(props.group.settings) : getEmptyGroupSettings()
  );
  const [changes, setChanges] = React.useState(false);
  const [triedSave, setTriedSave] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [confirmRemove, setConfirmRemove] = React.useState(false);
  const [members, setMembers] = React.useState([...storedMembers]);

  const adminOrPrivilage = (value: string) => role.admin || role?.privilages?.includes(value);

  const canWrite = adminOrPrivilage("CreateProjects");
  const canRemove = !isNew && adminOrPrivilage("DeleteProjects");
  // A move is a project save: CreateProjects on top of EditProjectSettings
  // (§6). The server also wants SetChainmanager when the group grants anyone
  // and answers with that text otherwise.
  const canMove = canWrite && adminOrPrivilage("EditProjectSettings");

  const memberDiff = React.useMemo(
    () => diffGroupMembers(storedMembers, members),
    [storedMembers, members]
  );
  const membersChanged = memberDiff.added.length > 0 || memberDiff.removed.length > 0;

  // Every project the caller sees, MW light rows excepted (no rights on
  // those). A project in another group says so — picking it moves it here.
  const projectOpts = React.useMemo(() => {
    const opts: FieldInfo[] = [];

    for (const proj of projects) {
      if (proj.mw_light)
        continue;

      const other =	proj.group && (!props.group || proj.group !== props.group.id)
      				?	getProjectGroupById(proj.group, groups)
      				:	null;

      opts.push({
        value:		proj.id,
        desc:		proj.id + ' - ' + proj.name +
        			(other ? ' ' + t('projects:groups:in_group', { name: other.name }) : ''),
      });
    }

    return opts;
  }, [projects, groups, props.group, t]);

  // Chain managers are global logins: local (project-scoped) users are never
  // offered. A stored name that is no longer a login stays offerable, or it
  // could never be removed.
  const loginOpts = React.useMemo(() => {
    const opts: FieldInfo[] = [];
    const seen = new Set<string>();

    for (const desc of logins.logins.descriptors || []) {
      if (desc.local_project_id)
        continue;

      opts.push({
        value:		desc.username,
        desc:		`${desc.username} (${desc.role})`,
      });
      seen.add(desc.username);
    }

    for (const def of groupSettingDefs) {
      for (const username of settings[def.key] || []) {
        if (seen.has(username))
          continue;

        opts.push({ value: username, desc: username });
        seen.add(username);
      }
    }

    return opts;
  }, [logins.logins.descriptors, settings]);

  const nameError = React.useMemo(
    () => validateGroupName(name, props.group ? props.group.id : undefined, groups),
    [name, props.group, groups]
  );

  const editableKeys = React.useMemo(
    () => canWrite
          ? groupSettingDefs.filter((def) => adminOrPrivilage(def.privilege)).map((def) => def.key)
          : [],
    [canWrite, role]
  );

  const updateName = (val: string) => {
    setName(val);
    setChanges(true);
  };

  const updateSetting = (key: keyof ProjectGroupSettings, val: string[]) => {
    setSettings({ ...settings, [key]: val });
    setChanges(true);
  };

  const handleSave = async () => {
    if (nameError) {
      setTriedSave(true);
      return;
    }

    setSaving(true);

    let groupId = props.group ? props.group.id : "";

    // The group first: a new one has no id to move projects into yet.
    if (isNew || changes) {
      const record = buildGroupRecord(props.group ? props.group.id : undefined, name, settings, editableKeys);
      const mess = await store.setProjectGroup(record);

      console.log("ProjectGroupEditor/handleSave/record=", record, "/mess=", mess);

      // code 0 is success, "have no updates" included (§4.2); anything else
      // was already shown verbatim by apiOperation.
      if (!checkNoError(mess)) {
        setSaving(false);
        return;
      }

      if (isNew)
        groupId = mess?.errors[0]?.result?.id || "";
    }

    // Then one project save per move. A refusal (e.g. "You don't have the
    // SetChainmanager privilege") is toasted by apiOperation and leaves that
    // project where it was; the others still move.
    if (	groupId &&
        	canMove
       ) {
      const moves = [
        ...memberDiff.added.map((id) => ({ id, group: groupId })),
        ...memberDiff.removed.map((id) => ({ id, group: "" })),
      ];

      for (const move of moves) {
        const proj = getProjectById(move.id, projects);
        if (!proj)
          continue;

        const mess = await store.setProject(getMoveProjectBody(proj, move.group));
        console.log("ProjectGroupEditor/handleSave/move=", move, "/mess=", mess);
      }
    }

    setSaving(false);
    setChanges(false);
    props.onClose();
  };

  const processRemove = async (answer: string) => {
    setConfirmRemove(false);

    if (	answer !== "yes" ||
        	!props.group
       )
      return;

    const mess = await store.setProjectGroup(getRemoveGroupRecord(props.group));
    if (!checkNoError(mess))
      return;

    props.onClose();
    navigate('/projects');
  };

  const removeMode = props.group ? groupRemoveMode(props.group) : 'delete';
  // §4.2 — the server refuses a disband while the group still carries
  // settings; the STORED settings decide, not the unsaved edits.
  const disbandBlocked =	!!props.group &&
  							removeMode === 'disband' &&
  							groupHasSettings(props.group.settings);
  const memberCount = props.group ? props.group.projects.length : 0;

  return (
  <React.Fragment>
    <FlexRow direction="column"
             align="start"
             className="px-[32px] pb-[120px]">
      <FlexRow direction="column"
		       align="start"
		       className="py-0 gap-[16px] max-w-[620px] w-full">
	    <CatHeader caption={isNew ? 'projects:groups:add' : 'projects:groups:edit'} />

		<Input label={t('projects:groups:name') + '*'}
		       placeholder={t('projects:groups:name_placeholder')}
		       isInvalid={triedSave && !!nameError}
		       errorMessage={nameError ? t(nameError, { max: PROJECT_GROUP_NAME_MAX }) : ''}
		       isReadOnly={!canWrite}
		       value={name}
		       onValueChange={updateName} />

	  {groupSettingDefs.map((def) => {
	    const editable = editableKeys.indexOf(def.key) > -1;

	    return (
		<FlexRow key={def.key}
		         direction="column"
		         align="start"
		         className="w-full gap-[4px]">
		  <Dropdown label={t('projects:groups:' + def.key)}
		            value={settings[def.key] || []}
		            placeholder={t('projects:project:project_settings:chain_manager:placeholder')}
		            selectionMode="multiple"
		            disallowEmptySelection={false}
		            isDisabled={!editable}
		            options={loginOpts}
		            isSearchable
		            searchPlaceholder={t('common:search')}
		            setValue={editable ? (val: string[]) => updateSetting(def.key, val) : () => {}}>
		    <>
		    </>
		  </Dropdown>
		  <span className="text-[12px] text-[--Dark-Grey]">
		    { t('projects:groups:' + def.key + '_hint') }
		  </span>
		</FlexRow>
	    );
	  })}

	  {canMove &&
		<FlexRow direction="column"
		         align="start"
		         className="w-full gap-[4px]">
		  <Dropdown label={t('projects:groups:members')}
		            value={members}
		            placeholder={t('projects:groups:members_placeholder')}
		            selectionMode="multiple"
		            disallowEmptySelection={false}
		            options={projectOpts}
		            isSearchable
		            searchPlaceholder={t('common:search')}
		            setValue={(val: string[]) => setMembers(val)}>
		    <>
		    </>
		  </Dropdown>
		  <span className="text-[12px] text-[--Dark-Grey]">
		    { t('projects:groups:members_pick_hint') }
		  </span>
		</FlexRow>
	  }
	  {!canMove && !isNew &&
		<FlexRow direction="column"
		         align="start"
		         className="w-full gap-[4px]">
		  <span className="text-[14px] font-medium">
		    { t('projects:groups:members') }
		  </span>
		  <FlexRow justify="start"
		           className="flex-wrap gap-[8px]">
		  {memberCount === 0 &&
		    <span className="text-[14px] text-[--Dark-Grey]">-</span>
		  }
		  {props.group && props.group.projects.map((id) =>
		    <span key={id}
		          className="text-[14px] text-[#1B69AA] underline cursor-pointer"
		          onClick={() => { props.onClose(); navigate('/projects/' + encodeURIComponent(id)); }}>
		      { id }
		    </span>
		  )}
		  </FlexRow>
		  <span className="text-[12px] text-[--Dark-Grey]">
		    { t('projects:groups:members_hint') }
		  </span>
		</FlexRow>
	  }
	  </FlexRow>
    </FlexRow>
    <FlexRow className="absolute bottom-0 p-[32px] bg-white z-10 justify-between gap-[16px] w-full">
      <FlexRow direction="column"
               align="start"
               className="gap-[4px]">
      {canRemove && props.group &&
        <React.Fragment>
          <Button caption={t(removeMode === 'delete' ? 'projects:groups:delete' : 'projects:groups:disband')}
                  icon="delete"
                  layout="warning"
                  isDisabled={disbandBlocked}
                  onClick={() => setConfirmRemove(true)} />
        {disbandBlocked &&
          <span className="text-[12px] text-[--Dark-Grey]">
            { t('projects:groups:disband_blocked') }
          </span>
        }
        </React.Fragment>
      }
      </FlexRow>
      <FlexRow justify="end"
               className="gap-[16px]">
        <Button className="uppercase"
                caption={t('modals:warning:cancel')}
                onClick={() => props.onClose()}
                layout="white" />
      {canWrite &&
        <Button caption={t('tags:save')}
                icon="save"
                isDisabled={saving || !(changes || membersChanged) || (triedSave && !!nameError)}
                onClick={() => handleSave()} />
      }
      </FlexRow>
    </FlexRow>
  {confirmRemove && props.group &&
    <Modal title={t(removeMode === 'delete' ? 'projects:groups:delete' : 'projects:groups:disband')}
           message={removeMode === 'delete'
                    ? t('projects:groups:delete_confirm', { name: props.group.name })
                    : t('projects:groups:disband_confirm', { name: props.group.name, count: memberCount })}
           onResult={() => processRemove("no")}>
      <FlexRow justify="end"
               className="gap-[16px]">
        <Button caption={t('modals:warning:cancel')}
                onClick={() => processRemove("no")}
                layout="white" />
        <Button caption={t(removeMode === 'delete' ? 'projects:groups:delete' : 'projects:groups:disband')}
                onClick={() => processRemove("yes")}
                layout="warning_bg" />
      </FlexRow>
    </Modal>
  }
  </React.Fragment>
  );
};

export default ProjectGroupEditor;
